/**
 * Rectangle tool (engine 46.3 "Rect"). On release, fills the marquee rectangle
 * with the stamp tiled across it (or clears it, when erasing). The stamp is
 * anchored at the rectangle's top-left so tiling is stable regardless of drag
 * direction. Pure over LayerTiles + Stroke.
 */

import { EMPTY_TILE, type TileID } from '../../model/constants';
import type { LayerTiles } from '../../model/chunk';
import type { Stroke } from '../../store/strokes';
import type { Stamp } from '../../model/types';

/** Normalize two corners into inclusive min/max bounds. */
export function normalizeRect(
    x0: number, y0: number, x1: number, y1: number,
): { minX: number; minY: number; maxX: number; maxY: number } {
    return {
        minX: Math.min(x0, x1), maxX: Math.max(x0, x1),
        minY: Math.min(y0, y1), maxY: Math.max(y0, y1),
    };
}

/**
 * Fill the inclusive rectangle [x0,y0]..[x1,y1]. When `erasing`, writes empty;
 * otherwise tiles the stamp across the rect (top-left anchored; row 0 = top =
 * higher world Y, matching paintStamp). Returns true if anything changed.
 */
export function fillRect(
    tiles: LayerTiles,
    stroke: Stroke,
    x0: number, y0: number, x1: number, y1: number,
    stamp: Stamp,
    erasing: boolean,
): boolean {
    const { minX, minY, maxX, maxY } = normalizeRect(x0, y0, x1, y1);
    let changed = false;

    for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
            let id: TileID;
            if (erasing) {
                id = EMPTY_TILE;
            } else {
                const c = ((x - minX) % stamp.w + stamp.w) % stamp.w;
                const r = ((maxY - y) % stamp.h + stamp.h) % stamp.h;
                id = stamp.tiles[r * stamp.w + c] ?? EMPTY_TILE;
            }
            const before = tiles.getTile(x, y);
            if (before === id) continue;
            if (tiles.setTile(x, y, id)) {
                stroke.record(x, y, before, id);
                changed = true;
            }
        }
    }
    return changed;
}

/**
 * Read a rectangular region of the map into a Stamp (the Picker's rect-drag,
 * engine 46.3 "Picker" / eyedropper). Row 0 of the resulting stamp is the top
 * row (highest world Y), so re-stamping reproduces the picked area.
 */
export function pickRect(
    tiles: LayerTiles,
    x0: number, y0: number, x1: number, y1: number,
): Stamp {
    const { minX, minY, maxX, maxY } = normalizeRect(x0, y0, x1, y1);
    const w = maxX - minX + 1;
    const h = maxY - minY + 1;
    const out: number[] = new Array(w * h).fill(EMPTY_TILE);
    for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
            const r = maxY - y; // top row = highest world Y
            const c = x - minX;
            out[r * w + c] = tiles.getTile(x, y);
        }
    }
    return { w, h, tiles: out };
}
